import { create } from 'zustand';
import {
  login,
  register,
  refreshTokenService,
  forgotPassword,
  resetPassword,
} from '../services/auth.service';
import {
  LoginRequest,
  RegisterRequest,
  User,
  ForgotPasswordRequest,
  ResetPasswordRequest,
} from '../types/auth.type';
import { clearAllUserData } from '../utils/auth-utils';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  login: (data: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => void;
  initializeAuth: () => void;
  forgotPassword: (data: ForgotPasswordRequest) => Promise<void>;
  resetPassword: (data: ResetPasswordRequest) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  accessToken: null,
  refreshToken: null,
  isLoading: false,
  error: null,
  isAuthenticated: false,
  isInitialized: false,

  login: async (data: LoginRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await login(data);
      const { access_token, refresh_token, user } = response;

      localStorage.setItem('accessToken', access_token);
      localStorage.setItem('refreshToken', refresh_token);
      localStorage.setItem('user', JSON.stringify(user));

      set({
        accessToken: access_token,
        refreshToken: refresh_token,
        user,
        isAuthenticated: true,
        isLoading: false,
        isInitialized: true,
      });
    } catch (error: any) {
      const status = error.response?.status;
      const serverMsg = error.response?.data?.detail || error.response?.data?.message;
      let errorMessage = 'Login failed';
      if (status === 400 || status === 401) errorMessage = serverMsg || 'Invalid email or password';
      else if (status === 403) errorMessage = serverMsg || 'Access forbidden';
      else if (status === 404) errorMessage = serverMsg || 'User not found';
      else if (status === 500) errorMessage = serverMsg || 'Server error. Please try again later';
      else if (error.request) errorMessage = 'لا يوجد اتصال بالسيرفر';
      else errorMessage = error.message || 'Login failed';
      set({ error: errorMessage, isLoading: false, isAuthenticated: false });
      throw error;
    }
  },

  register: async (data: RegisterRequest) => {
    set({ isLoading: true, error: null });
    try {
      await register(data);
      set({ isLoading: false });
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail || error.response?.data?.message || error.message || 'Registration failed';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  logout: () => {
    clearAllUserData();
    set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false, isInitialized: false });
  },

  initializeAuth: () => {
    const accessToken  = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    const userStr      = localStorage.getItem('user');
    let user: User | null = null;
    if (userStr) {
      try { user = JSON.parse(userStr); } catch { user = null; }
    }
    if (accessToken && refreshToken && user) {
      set({ accessToken, refreshToken, isAuthenticated: true, user, isInitialized: true });
    } else {
      set({ isAuthenticated: false, user: null, isInitialized: true });
    }
  },

  forgotPassword: async (data: ForgotPasswordRequest) => {
    set({ isLoading: true, error: null });
    try {
      await forgotPassword(data);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.response?.data?.detail || error.message || 'Failed to send reset email', isLoading: false });
      throw error;
    }
  },

  resetPassword: async (data: ResetPasswordRequest) => {
    set({ isLoading: true, error: null });
    try {
      await resetPassword(data);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message || 'Reset password failed', isLoading: false });
      throw error;
    }
  },
}));

// Re-export refreshTokenService for axios interceptor
export { refreshTokenService };
